import { creatureIcon, creatureSprite } from "../art/creatures.ts";
import { enterAge, settleT } from "../art/craftFeel.ts";
import { TYPE_COL, UI, withA } from "../art/pal.ts";
import { moveById } from "../data/moves.ts";
import { SPECIES } from "../data/species.ts";
import { fitText, measureText, wrapPx } from "../engine/font.ts";
import { SCREEN_H, SCREEN_W } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import type { Mon } from "../game/mon.ts";
import type { Scene } from "../game/scenes.ts";
import { MARKET_FEE, MARKET_MAX_LISTINGS, MARKET_MAX_PRICE, MARKET_MIN_PRICE, type Listing } from "../net/protocol.ts";
import { backdrop, coin, focus, glass, hint, titleBar, typeBadge } from "../ui/kit.ts";

const ROWS = 6;
const TABS = ["BROWSE", "SELL", "MINE"] as const;
type Tab = (typeof TABS)[number];

/** Suggested asking price: level and rarity, rounded to a friendly number. */
export function suggestPrice(m: Mon): number {
  const sp = SPECIES[m.speciesId];
  const rare = Math.max(0, 60 - sp.catch) * 12;
  return Math.max(MARKET_MIN_PRICE, Math.round(((80 + m.level * 22 + rare + sp.bounty * 4) * (m.shiny ? 3 : 1)) / 10) * 10);
}

/**
 * The Meme Market (Neon Harbor). Browse every scout's listings, sell from your crew or box at a
 * fixed price, or pull your own listings back. The server holds escrow; this screen only asks.
 */
export function marketScene(ctx: Ctx): Scene {
  let tab: Tab = "BROWSE";
  let cursor = 0;
  let scroll = 0;
  let age = 0;
  let pricing: { mon: Mon; price: number } | null = null;
  let buying: Listing | null = null;
  let toast: { text: string; at: number } | null = null;
  let unsub: () => void = () => {};

  const listings = (): Listing[] => ctx.net.market ?? [];
  const others = (): Listing[] => listings().filter((l) => l.seller !== ctx.net.id);
  const mine = (): Listing[] => listings().filter((l) => l.seller === ctx.net.id);
  const crew = (): Mon[] => [...ctx.state.party, ...ctx.state.pc];
  const rows = (): number => (tab === "BROWSE" ? others().length : tab === "SELL" ? crew().length : mine().length);

  function selected(): { mon: Mon; listing?: Listing } | null {
    if (tab === "BROWSE") { const l = others()[cursor]; return l ? { mon: l.mon, listing: l } : null; }
    if (tab === "MINE") { const l = mine()[cursor]; return l ? { mon: l.mon, listing: l } : null; }
    const m = crew()[cursor];
    return m ? { mon: m } : null;
  }

  function clampCursor(): void {
    const n = rows();
    cursor = Math.max(0, Math.min(cursor, n - 1));
    if (cursor < scroll) scroll = cursor;
    if (cursor >= scroll + ROWS) scroll = cursor - ROWS + 1;
    scroll = Math.max(0, Math.min(scroll, Math.max(0, n - ROWS)));
  }

  return {
    id: "market",
    native: true,
    enter() {
      age = enterAge(ctx.shot);
      tab = "BROWSE";
      cursor = scroll = 0;
      pricing = buying = null;
      toast = null;
      unsub = ctx.net.on((m) => {
        if (m.t === "market") clampCursor();
        if (m.t === "notice") {
          toast = { text: m.text, at: ctx.now };
          ctx.net.notices = ctx.net.notices.filter((n) => n !== m.text);
        }
      });
      if (ctx.net.live) ctx.net.send({ t: "market" });
    },
    exit() {
      unsub();
    },
    update() {
      age += ctx.dt;
      if (!ctx.net.live) {
        if (ctx.input.consumeAny("Enter", "Escape")) {
          ctx.audio.back();
          ctx.pop();
        }
        return;
      }
      const d = ctx.input.menuDir(ctx.now);
      if (pricing) {
        const big = ctx.input.down.has("Shift") ? 10 : 1;
        const step = d?.y ? -d.y * 10 * big : d?.x ? d.x * 100 * big : 0;
        if (step) {
          pricing.price = Math.max(MARKET_MIN_PRICE, Math.min(MARKET_MAX_PRICE, pricing.price + step));
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) { ctx.audio.back(); pricing = null; return; }
        if (ctx.input.consume("Enter")) {
          ctx.audio.confirm();
          ctx.net.send({ t: "marketList", uid: pricing.mon.uid, price: pricing.price });
          pricing = null;
        }
        return;
      }
      if (buying) {
        if (ctx.input.consume("Escape")) { ctx.audio.back(); buying = null; return; }
        if (ctx.input.consume("Enter")) {
          if (ctx.state.money < buying.price) {
            ctx.audio.fail();
            toast = { text: "Not enough coins.", at: ctx.now };
          } else {
            ctx.audio.confirm();
            ctx.net.send({ t: "marketBuy", id: buying.id });
          }
          buying = null;
        }
        return;
      }
      if (d?.x) {
        tab = TABS[(TABS.indexOf(tab) + d.x + TABS.length) % TABS.length]!;
        cursor = scroll = 0;
        ctx.audio.cursor();
        return;
      }
      if (d?.y && rows()) {
        cursor = (cursor + d.y + rows()) % rows();
        clampCursor();
        ctx.audio.cursor();
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        ctx.pop();
        return;
      }
      if (!ctx.input.consume("Enter")) return;
      const sel = selected();
      if (!sel) { ctx.audio.fail(); return; }
      if (tab === "BROWSE") { ctx.audio.confirm(); buying = sel.listing!; }
      else if (tab === "SELL") {
        if (mine().length >= MARKET_MAX_LISTINGS || crew().length < 2) {
          ctx.audio.fail();
          toast = { text: crew().length < 2 ? "Keep at least one creature." : `Up to ${MARKET_MAX_LISTINGS} listings at once.`, at: ctx.now };
          return;
        }
        ctx.audio.confirm();
        pricing = { mon: sel.mon, price: suggestPrice(sel.mon) };
      } else {
        ctx.audio.confirm();
        ctx.net.send({ t: "marketCancel", id: sel.listing!.id });
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const t = settleT(age, reduced, 120);
      backdrop(g, ctx.now, reduced, [70, 24, 70, 255], [10, 8, 24, 255]);
      titleBar(g, "MEME MARKET", `${ctx.state.money} coins`, UI.pink);
      if (!ctx.net.live) {
        glass(g, 56, 70, 272, 80, { r: 10 });
        g.textShadow(70, 82, "The Market is closed offline.", UI.text, UI.ink);
        let y = 98;
        for (const row of wrapPx("It runs on the live world server: connect to list your creatures and buy from other scouts.", 244)) {
          g.text(70, y, row, UI.textDim);
          y += 11;
        }
        hint(g, 70, 134, "A", "back", "a");
        return;
      }
      // Tabs.
      let tx = 10;
      for (const name of TABS) {
        const on = name === tab;
        const count = name === "BROWSE" ? others().length : name === "SELL" ? crew().length : mine().length;
        const label = `${name} ${count}`;
        const w = measureText(label) + 14;
        g.rrect(tx, 26, w, 14, 5, on ? UI.pink : withA(UI.bg2, 220));
        g.text(tx + 7, 29, label, on ? UI.paperInk : UI.textDim);
        tx += w + 4;
      }
      g.textR(374, 29, `${Math.round(MARKET_FEE * 100)}% fee burned on sales`, UI.textMute);

      // Left: rows.
      const lx = 8 - Math.round((1 - t) * 30);
      glass(g, lx, 44, 184, 136, { r: 8 });
      const list: { mon: Mon; price?: number; who?: string }[] = tab === "BROWSE"
        ? others().map((l) => ({ mon: l.mon, price: l.price, who: l.sellerName }))
        : tab === "MINE" ? mine().map((l) => ({ mon: l.mon, price: l.price })) : crew().map((mon) => ({ mon }));
      if (!list.length) {
        const empty = tab === "BROWSE" ? "No listings yet. Be the first seller!" : tab === "MINE" ? "Nothing listed. Pick SELL." : "No creatures.";
        let y = 60;
        for (const row of wrapPx(empty, 164)) { g.text(lx + 10, y, row, UI.textMute); y += 11; }
      }
      for (let r = 0; r < ROWS; r++) {
        const i = scroll + r;
        const row = list[i];
        if (!row) break;
        const y = 50 + r * 21;
        const on = cursor === i;
        g.rrect(lx + 6, y, 172, 19, 5, on ? withA(UI.bg3, 220) : withA(UI.bg1, 160));
        g.blit(creatureIcon(row.mon.speciesId, !!row.mon.shiny), lx + 9, y + 1);
        g.text(lx + 30, y + 6, fitText(`${on ? ">" : ""}${row.mon.nick}${row.mon.shiny ? " ✦" : ""}`, row.price !== undefined ? 70 : 100), on ? UI.goldHi : UI.text);
        g.textR(lx + (row.price !== undefined ? 116 : 172), y + 6, `Lv${row.mon.level}`, UI.gold);
        if (row.price !== undefined) g.textR(lx + 172, y + 6, `${row.price}$`, UI.goldHi);
        if (on) focus(g, lx + 5, y - 1, 174, 21, ctx.now, reduced, 6);
      }
      if (scroll > 0) g.text(lx + 88, 45, "▲", UI.textDim);
      if (scroll + ROWS < list.length) g.text(lx + 88, 174, "▼", UI.textDim);

      // Right: detail card.
      const rx = 198 + Math.round((1 - t) * 30);
      glass(g, rx, 44, 178, 136, { r: 8 });
      const sel = selected();
      if (sel) {
        const m = sel.mon, sp = SPECIES[m.speciesId];
        g.disc(rx + 38, 82, 30, withA(TYPE_COL[sp.types[0]!]!.fill, 60));
        g.blit(creatureSprite(m.speciesId, "front", 2, !!m.shiny), rx + 6, 50);
        g.textShadow(rx + 76, 52, fitText(m.nick, 96), UI.text, UI.ink);
        g.text(rx + 76, 64, `Lv${m.level}  ${sp.name !== m.nick ? sp.name : ""}`, UI.gold);
        let bx = rx + 76;
        for (const type of sp.types) bx += typeBadge(g, bx, 76, type, true) + 2;
        g.text(rx + 76, 92, `DRIP ${m.drip.hp + m.drip.atk + m.drip.def + m.drip.spd}/60`, UI.textDim);
        m.moves.slice(0, 4).forEach((slot, k) => {
          const mv = moveById(slot.id);
          g.rrect(rx + 8 + (k % 2) * 84, 118 + Math.floor(k / 2) * 14, 80, 12, 4, withA(TYPE_COL[mv.type]!.fill, 90));
          g.text(rx + 12 + (k % 2) * 84, 121 + Math.floor(k / 2) * 14, fitText(mv.name, 72), UI.text);
        });
        if (sel.listing) {
          coin(g, rx + 10, 150, 0);
          g.textShadow(rx + 23, 151, `${sel.listing.price} coins`, UI.goldHi, UI.ink);
          g.textR(rx + 170, 151, fitText(tab === "MINE" ? "your listing" : `by ${sel.listing.sellerName}`, 80), UI.textDim);
        }
        hint(g, rx + 10, 164, "A", tab === "BROWSE" ? "buy" : tab === "SELL" ? "set a price" : "take back", "a");
      } else {
        g.text(rx + 12, 60, "Nothing selected.", UI.textMute);
      }
      hint(g, 8, SCREEN_H - 30, "◀▶", "tabs", "a");
      hint(g, 8, SCREEN_H - 16, "B", "leave the market", "b");

      if (pricing) {
        g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, 150]);
        glass(g, 70, 56, 244, 104, { r: 10, border: UI.pink });
        g.textShadow(84, 66, `List ${pricing.mon.nick} for`, UI.text, UI.ink);
        coin(g, 84, 86, reduced ? 0 : ctx.now / 400);
        g.textBig(100, 82, `${pricing.price}`, UI.goldHi, 2);
        g.text(100 + measureText(`${pricing.price}`, 2) + 6, 90, "coins", UI.textDim);
        const cut = pricing.price - Math.ceil(pricing.price * MARKET_FEE);
        g.text(84, 108, `You get ${cut} after the ${Math.round(MARKET_FEE * 100)}% burn.`, UI.textDim);
        g.text(84, 120, "▲▼ 10   ◀▶ 100   Shift ×10", UI.textMute);
        hint(g, 84, 138, "A", "list it", "a");
        hint(g, 170, 138, "B", "cancel", "b");
      }
      if (buying) {
        g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, 150]);
        glass(g, 70, 64, 244, 88, { r: 10, border: UI.gold });
        g.textShadow(84, 74, `Buy ${buying.mon.nick} Lv${buying.mon.level}?`, UI.text, UI.ink);
        coin(g, 84, 92, 0);
        g.textShadow(98, 93, `${buying.price} coins from ${buying.sellerName}`, UI.goldHi, UI.ink);
        g.text(84, 108, ctx.state.money >= buying.price ? `You keep ${ctx.state.money - buying.price} coins.` : "Not enough coins.", ctx.state.money >= buying.price ? UI.textDim : UI.red);
        hint(g, 84, 130, "A", "buy", "a");
        hint(g, 150, 130, "B", "no", "b");
      }
      if (toast && ctx.now - toast.at < 3500) {
        const w = Math.min(360, measureText(toast.text) + 20);
        glass(g, (SCREEN_W - w) / 2, 186, w, 16, { r: 6, alpha: 230 });
        g.text((SCREEN_W - w) / 2 + 10, 190, fitText(toast.text, w - 20), UI.text);
      }
    },
  };
}
